/**
 * Uni Forms — audience, cross-module figures and admin reporting.
 *
 * These three screens are the SPA half of the features added to
 * ``uni_forms`` for internal/public forms, figures pulled from other
 * modules, and the admin report surface:
 *
 *   * UniFormsAudience — per-form "who may fill this?" (public /
 *     internal) plus the independent "show on For Me" picker, the
 *     optional admin lock, and reusable people groups that can be
 *     shared (view / edit / delete) and allocated to many forms.
 *   * UniFormsFigures  — per-form "take this number from another
 *     module" rows (e.g. doctor calls from Pharma SFA for the day the
 *     form is filled) plus their locked / changeable switch.
 *   * UniFormsReportTemplates — saved report templates (list, share,
 *     run, duplicate). Creation/editing uses ``UniFormsReportTemplateWizard.jsx``.
 *
 * Why a separate file from ``UniFormsPages.jsx``
 * ----------------------------------------------
 * That file is the *builder* surface (lists of forms, quizzes, polls,
 * templates) and is already large.  These screens are administrative,
 * share a different set of endpoints, and are reached from their own
 * nav entries, so keeping them apart keeps both files reviewable.
 *
 * Server is the authority
 * -----------------------
 * Every guard shown here is cosmetic — the API enforces the same rules
 * (``Form.can_manage_visibility``, ``FormModuleFigure.mode_locked_by_admin``,
 * ``FormReportView.visible_to``).  When the server says a control is
 * unavailable we disable it *and* show why, rather than hiding it, so
 * the user understands the restriction instead of thinking the UI is
 * broken.
 */
import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { deleteJSON, downloadFile, getJSON, patchJSON, postJSON } from '../../api/client.js';
import { ROUTES } from '../../config.js';
import {
  fmtCount, isoDate,
  UniActionButton, UniBool, UniCheckField, UniConfirmModal, UniCopyField, UniDateField,
  UniFilterSelect, UniFormError, UniKpiCard, UniModal, UniPageShell,
  UniPickList, UniRowActions, UniSection, UniSelectField, UniSpecEditModal,
  UniSpecViewModal, UniStatusPill, UniTable, UniTextArea, UniTextField,
  UniToolbar, uniSpecKeys, useUniAction, useUniCreate, useUniDelete,
  useUniEdit, useUniList,
} from './UniShared.jsx';
import {
  filterQueryString, ReportRuntimeFilters,
} from './UniFormsReportFilters.jsx';
import { UniFormsReportTable } from './UniFormsReportTable.jsx';
import { ReportPdfExportModal, reportPdfFallbackName } from './UniFormsReportPdfExport.jsx';
import { formatGeneratedLabel, formatReportPeriod, isQuestionColumn, presentReportText } from './reportText.js';
import {
  ReportTemplateLockModal, ReportTemplatePinBadge,
} from './ReportTemplatePinLock.jsx';

const EP = {

  FORMS:      '/api/uni-forms/forms/',
  GROUPS:     '/api/uni-forms/audience-groups/',
  USERS:      '/api/uni-forms/audience-users/',
  FIGURES:    '/api/uni-forms/module-figures/',
  CATALOGUE:  '/api/uni-forms/module-figures/catalogue/',
  REPORTS:    '/api/uni-forms/reports/',
  AD_HOC:     '/api/uni-forms/reports/ad-hoc/',
  AD_HOC_SHEET: '/api/uni-forms/reports/ad-hoc/export-sheet/',
  COLUMNS:    '/api/uni-forms/reports/columns/',
  FILTER_CAT: '/api/uni-forms/reports/filter-catalog/',
  SUMMARY:    '/api/uni-forms/report-summary/',
  QUESTIONS:  '/api/uni-forms/questions/',
};

const THEME = 'forms';

/* Choice lists mirrored from ``uni_forms/models.py``.  Small, stable,
   and part of the model's public contract — cheaper to inline than to
   fetch, and it keeps the selects usable before any request settles. */
const VISIBILITY_OPTIONS = [
  { value: 'public',     label: 'Public — anyone with the link can fill it' },
  { value: 'internal',   label: 'Internal — only signed-in members of this organisation' },
];

const LEGACY_RESTRICTED_OPTION = {
  value: 'restricted',
  label: 'Restricted — only selected internal users may fill it',
};

function visibilityOptionsFor(current) {
  if (current === 'restricted') {
    return [...VISIBILITY_OPTIONS, LEGACY_RESTRICTED_OPTION];
  }
  return VISIBILITY_OPTIONS;
}

const GROUP_PURPOSE_OPTIONS = [
  { value: 'both',     label: 'Both audience and reporting' },
  { value: 'audience', label: 'Form audience (who may fill)' },
  { value: 'report',   label: 'Report grouping (how results are sliced)' },
];

/* Audience-group fields for the Edit / View modals, mirrored from
   ``FormAudienceGroupSerializer``.  ``members`` is writable there but is
   deliberately absent here: membership is edited through the dedicated
   "People" picker (the ``set-members`` action), and PATCHing a raw ID
   list from a text box would be a foot-gun. */
const GROUP_FIELDS = [
  { key: 'name',        label: 'Group name', required: true },
  { key: 'description', label: 'Notes', type: 'textarea' },
  { key: 'purpose',     label: 'Used for', type: 'select',
    options: GROUP_PURPOSE_OPTIONS },
  { key: 'member_count', label: 'People', editable: false,
    render: (r) => fmtCount(r.member_count) },
  { key: 'form_details', label: 'Used on forms', editable: false,
    render: (r) => {
      const names = (r.form_details || []).map((f) => f.title).filter(Boolean);
      return names.length ? names.join(', ') : '—';
    } },
  { key: 'my_permission', label: 'Your access', editable: false,
    render: (r) => (r.is_owner ? 'Owner' : (r.my_permission || 'view')) },
];

const GROUP_SHARE_OPTIONS = [
  { value: 'view', label: 'View — use on forms; cannot change the group' },
  { value: 'edit', label: 'Edit — change name, people, and which forms use it' },
  { value: 'delete', label: 'Delete — full access, including deleting the group' },
];


const FIGURE_MODE_OPTIONS = [
  { value: 'locked',               label: 'Locked — read-only, always from the source module' },
  { value: 'editable',             label: 'Changeable — pre-filled, respondent may edit' },
  { value: 'editable_with_reason', label: 'Changeable with reason — edit needs a remark' },
];

const DATE_BASIS_OPTIONS = [
  { value: 'submission_date', label: 'The day the form is filled' },
  { value: 'question',        label: 'A date answered inside this form' },
];

const REPORT_MODE_OPTIONS = [
  { value: 'person_date', label: 'Person × date' },
  { value: 'date_all',    label: 'Everyone, one date' },
  { value: 'group',       label: 'A group over a period' },
];

const REPORT_TYPE_OPTIONS = [
  { value: 'flat_table', label: 'Flat table — one row per submission' },
  { value: 'pivot_by_user', label: 'Pivot by user — one row per person' },
  { value: 'pivot_by_question', label: 'Pivot by question — one row per question' },
  { value: 'summary', label: 'Summary — counts and choice tallies' },
  { value: 'question_by_city', label: 'Question by city — matrix' },
  { value: '', label: 'Legacy — fold rows (person × date / group)' },
];

const PIVOT_POPULATION_OPTIONS = [
  { value: 'audience', label: 'Form audience — people picked for this form' },
  { value: 'organisation', label: 'Whole organisation — every active user' },
];

const PIVOT_STRATEGY_OPTIONS = [
  { value: 'latest', label: 'Latest complete response' },
  { value: 'aggregate', label: 'Aggregate all responses in the period' },
];

function ReportResultsTable({ payload, columns, rowKey = 'response_id' }) {
  return (
    <UniFormsReportTable payload={payload} columns={columns} rowKey={rowKey} />
  );
}

const FORM_TYPE_OPTIONS = [
  { value: 'contact',      label: 'Contact / Generic' },
  { value: 'registration', label: 'User Registration' },
  { value: 'login',        label: 'User Login' },
  { value: 'calculation',  label: 'Calculation / Quote' },
  { value: 'survey',       label: 'Survey' },
];



/* ====================================================================
   Small local helpers
   ==================================================================== */

/** Cancel + busy-aware save, shared by the modals on these screens. */
function ModalFooter({ busy, onCancel, onSave, saveLabel = 'Save', disabled }) {
  return (
    <>
      <button type="button" className="ty-btn ty-btn--secondary"
              onClick={onCancel} disabled={busy}>
        Cancel
      </button>
      <button type="button" className="ty-btn" onClick={onSave}
              disabled={busy || disabled}>
        {busy ? 'Saving…' : saveLabel}
      </button>
    </>
  );
}

/** Inline explanation strip (why a control is disabled, what's missing). */
function Note({ tone = 'info', icon, children }) {
  return (
    <div className={`uni-note uni-note--${tone}`}>
      <span aria-hidden="true">{icon || (tone === 'lock' ? '🔒' : tone === 'warn' ? '⚠️' : 'ℹ️')}</span>
      <span>{children}</span>
    </div>
  );
}

/** A settings block with a title, optional right-hand actions, body. */
function Card({ title, sub, actions, children, foot }) {
  return (
    <section className="uni-card">
      <div className="uni-card__head">
        <div>
          <h3 className="uni-card__title">{title}</h3>
          {sub && <div className="uni-card__sub">{sub}</div>}
        </div>
        {actions && <div className="uni-row-actions">{actions}</div>}
      </div>
      {children}
      {foot && <div className="uni-card__foot">{foot}</div>}
    </section>
  );
}

/**
 * Load every form once for the "which form?" selects.
 *
 * ``page_size=200`` rather than paging: these selects are useless when
 * partially filled, and a tenant with more than 200 forms is far past
 * the point where a dropdown is the right control anyway (they'd search
 * from the Forms list and deep-link instead).
 */
function useFormOptions() {
  const { rows, loading } = useUniList(`${EP.FORMS}?page_size=200`);
  const options = React.useMemo(
    () => rows.map((f) => ({ value: f.id, label: f.title })),
    [rows],
  );
  return { forms: rows, options, loading };
}

/** People picker source — shared by audiences and report populations. */
function useUserOptions() {
  const { rows, loading } = useUniList(EP.USERS);
  const options = React.useMemo(
    () => rows.map((u) => ({
      value: u.id,
      label: u.label || u.username,
      sub: u.email || u.username,
    })),
    [rows],
  );
  return { options, loading };
}


/* ====================================================================
   1. Audience & visibility
   ====================================================================
   Answers "who may fill this form" (public or internal) and, separately,
   "list it on For Me for these people".

   The visibility state is fetched from the dedicated
   ``/forms/<id>/visibility/`` action rather than being read off the
   form row: that endpoint also returns ``can_manage`` / ``can_lock``
   for the current user, which is exactly what decides whether these
   controls are live.  Saving POSTs back to the same action so a locked
   form fails loudly with a 403 instead of silently ignoring fields.
   */

export function UniFormsAudience() {
  const { forms: allForms, options: formOptions, loading: formsLoading } = useFormOptions();
  const { options: userOptions } = useUserOptions();
  const groups = useUniList(`${EP.GROUPS}?purpose=audience`);

  const manageableFormOptions = React.useMemo(
    () => allForms
      .filter((f) => f.can_edit !== false)
      .map((f) => ({ value: f.id, label: f.title })),
    [allForms],
  );
  const mineGroups = React.useMemo(
    () => groups.rows.filter((g) => g.is_owner),
    [groups.rows],
  );
  const sharedGroups = React.useMemo(
    () => groups.rows.filter((g) => !g.is_owner),
    [groups.rows],
  );

  const [formId, setFormId] = React.useState('');
  const [state, setState]   = React.useState(null);      // server payload
  const [draft, setDraft]   = React.useState(null);      // local edits
  const [loading, setLoading] = React.useState(false);
  const save = useUniAction();

  // Default to the first form so the screen is never an empty shell.
  React.useEffect(() => {
    if (!formId && formOptions.length) setFormId(formOptions[0].value);
  }, [formOptions, formId]);

  const load = React.useCallback(() => {
    if (!formId) return;
    setLoading(true);
    getJSON(`${EP.FORMS}${formId}/visibility/`)
      .then((data) => {
        setState(data);
        setDraft({
          visibility: data.visibility,
          show_to_selected_internal_users: !!(
            data.show_to_selected_internal_users
            ?? (data.visibility === 'restricted')
          ),
          internal_notice: data.internal_notice || '',
          visibility_locked: !!data.visibility_locked,
          audience_group_ids: data.audience_group_ids || [],
          audience_user_ids: data.audience_user_ids || [],
        });
      })
      .catch((err) => { setState(null); save.setError(err); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  React.useEffect(load, [load]);

  React.useEffect(() => {
    if (window.location.hash !== '#audience-groups') return;
    document.getElementById('audience-groups')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const canManage = !!state?.can_manage;
  const forMe = !!draft?.show_to_selected_internal_users;

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const onSave = () => save.run(async () => {
    const data = await postJSON(`${EP.FORMS}${formId}/visibility/`, draft);
    setState(data);
    setDraft({
      visibility: data.visibility,
      show_to_selected_internal_users: !!(
        data.show_to_selected_internal_users
        ?? (data.visibility === 'restricted')
      ),
      internal_notice: data.internal_notice || '',
      visibility_locked: !!data.visibility_locked,
      audience_group_ids: data.audience_group_ids || [],
      audience_user_ids: data.audience_user_ids || [],
    });
  }).catch(() => {});

  // ── Groups (reusable sets of people) ──────────────────────────────
  const createGroup = useUniCreate(
    EP.GROUPS,
    { name: '', description: '', purpose: 'both', members: [], form_ids: [] },
    () => groups.reload(),
  );

  const [editGroup, setEditGroup] = React.useState(null);
  const [editGroupForms, setEditGroupForms] = React.useState(null);
  const groupAction = useUniAction();

  // Renaming / re-purposing a group is a plain PATCH on the group row,
  // which is a different job from picking its members (that goes through
  // the ``set-members`` action) or which forms use it.  Separate modals
  // so neither has to guess which the user meant.

  const [viewingGroup, setViewingGroup] = React.useState(null);
  const editGroupDetails = useUniEdit(EP.GROUPS, () => groups.reload());
  const delGroup = useUniDelete(EP.GROUPS, () => groups.reload());

  const [shareGroup, setShareGroup] = React.useState(null);
  const [shareLinks, setShareLinks] = React.useState([]);
  const [shareDraft, setShareDraft] = React.useState({
    shared_with: '', permission: 'view',
  });
  const shareAction = useUniAction();

  const saveGroupMembers = () => groupAction.run(async () => {
    await postJSON(`${EP.GROUPS}${editGroup.id}/set-members/`,
                   { user_ids: editGroup.members });
    setEditGroup(null);
    groups.reload();
  }).catch(() => {});

  const saveGroupForms = () => groupAction.run(async () => {
    await patchJSON(`${EP.GROUPS}${editGroupForms.id}/`,
                    { form_ids: editGroupForms.form_ids });
    setEditGroupForms(null);
    groups.reload();
  }).catch(() => {});

  const openShare = (row) => {
    setShareGroup(row);
    setShareLinks([]);
    setShareDraft({ shared_with: '', permission: 'view' });
    shareAction.run(async () => {
      setShareLinks(await getJSON(`${EP.GROUPS}${row.id}/shares/`));
    }).catch(() => {});
  };

  const createShareLink = () => shareAction.run(async () => {
    if (!shareGroup || !shareDraft.shared_with) return;
    const created = await postJSON(`${EP.GROUPS}${shareGroup.id}/shares/`, {
      shared_with: shareDraft.shared_with,
      permission: shareDraft.permission,
    });
    setShareLinks((prev) => {
      const rest = prev.filter((s) => s.shared_with !== created.shared_with);
      return [created, ...rest];
    });
    setShareDraft({ shared_with: '', permission: 'view' });
    groups.reload();
  }).catch(() => {});

  const revokeShareLink = (shareId) => {
    if (!window.confirm('Stop sharing with this person?')) return;
    shareAction.run(async () => {
      await deleteJSON(`${EP.GROUPS}${shareGroup.id}/shares/${shareId}/`);
      setShareLinks((prev) => prev.filter((s) => s.id !== shareId));
      groups.reload();
    }).catch(() => {});
  };

  const groupColumns = [

    { key: 'name', label: 'Group',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    { key: 'purpose', label: 'Used for',
      render: (r) => <UniStatusPill value={r.purpose} palette="cyan" /> },
    { key: 'member_count', label: 'People', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.member_count)}</span> },
    { key: 'forms', label: 'Forms',
      render: (r) => {
        const details = r.form_details || [];
        if (!details.length) return '—';
        const names = details.map((f) => f.title).join(', ');
        return (
          <span title={names}>
            {fmtCount(details.length)} form{details.length === 1 ? '' : 's'}
          </span>
        );
      } },
    { key: 'access', label: 'Access',
      render: (r) => (
        <UniStatusPill
          value={r.is_owner ? 'owner' : (r.my_permission || 'view')}
          palette="violet"
        />
      ) },
    { key: 'description', label: 'Notes',
      render: (r) => r.description || '—' },
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => (
        <UniRowActions items={[
          { key: 'view', label: 'View', title: 'Group settings and size',
            onClick: () => setViewingGroup(r) },
          { key: 'edit', label: 'Edit', title: 'Rename or re-purpose this group',
            show: !!r.can_edit,
            onClick: () => editGroupDetails.start(r, uniSpecKeys(GROUP_FIELDS)) },
          { key: 'people', label: 'People', title: 'Add or remove members',
            show: !!r.can_edit,
            onClick: () => setEditGroup({ id: r.id, name: r.name,
                                          members: (r.members || []).map(String) }) },
          { key: 'forms', label: 'Forms',
            title: 'Use this group on more than one form',
            show: !!r.can_edit,
            onClick: () => setEditGroupForms({
              id: r.id, name: r.name,
              form_ids: (r.form_ids || []).map(String),
            }) },
          { key: 'share', label: 'Share',
            title: 'Share view, edit, or delete access',
            show: !!r.can_share,
            onClick: () => openShare(r) },
          { key: 'delete', label: 'Delete', danger: true,
            show: !!r.can_delete,
            title: 'Delete this group (the people themselves are kept)',
            onClick: () => delGroup.ask(r) },
        ]} />
      ) },
  ];


  return (
    <UniPageShell
      theme={THEME}
      icon="🔐"
      title="Audience & visibility"
      subtitle="Who may fill a form, and who sees it on For Me."
      actions={
        <>
          <UniActionButton hero to="/uni-forms/forms">📝 Forms</UniActionButton>
          <UniActionButton hero to="/uni-forms">← Dashboard</UniActionButton>
        </>
      }
    >
      <UniSection title="Who may fill this form" />

      <UniToolbar>
        <UniFilterSelect
          label="Form"
          value={formId}
          onChange={setFormId}
          width={280}
          options={formsLoading && !formOptions.length
            ? [{ value: '', label: 'Loading…' }]
            : formOptions.length
              ? formOptions
              : [{ value: '', label: 'No forms yet' }]}
        />
      </UniToolbar>

      <Card
        title="Visibility"
        sub="Internal forms always require a sign-in. Listing a form on For Me is a separate option below."
        foot={formId && (
          <button type="button" className="ty-btn"
                  onClick={onSave} disabled={!canManage || save.busy || loading}>
            {save.busy ? 'Saving…' : 'Save visibility'}
          </button>
        )}
      >
        <UniFormError error={save.error} />

        {!formId ? (
          <Note tone="warn">Create a form first — there's nothing to restrict yet.</Note>
        ) : loading || !draft ? (
          <div className="uni-state">
            <div className="ty-spinner" aria-hidden="true" />
            <div className="uni-state__hint">Loading…</div>
          </div>
        ) : (
          <>
            {!canManage && (
              <Note tone="lock">
                An administrator has locked this form's audience. You can see
                the settings but only an admin can change them.
              </Note>
            )}

            <UniSelectField
              label="Who can open and fill it"
              value={draft.visibility}
              onChange={(v) => set('visibility', v)}
              options={canManage
                ? visibilityOptionsFor(draft.visibility)
                : visibilityOptionsFor(draft.visibility).filter(
                    (o) => o.value === draft.visibility,
                  )}
              hint={
                draft.visibility === 'public'
                  ? 'Anyone with the link — use this for public contact and lead forms.'
                  : draft.visibility === 'internal'
                    ? 'Every signed-in member of this organisation may fill it.'
                    : 'Only the people and groups picked below may fill it.'
              }
            />

            {draft.visibility !== 'public' && (
              <UniTextField
                label="Internal notice"
                value={draft.internal_notice}
                onChange={(v) => set('internal_notice', v)}
                placeholder="e.g. For internal reporting only — do not share"
                hint="Shown as a banner at the top of the form."
              />
            )}

            <UniCheckField
              label="Display this form for selected internal users"
              checked={forMe}
              onChange={(v) => set('show_to_selected_internal_users', v)}
              hint="They will find it under “For Me”. Works with public and internal forms."
            />

            {forMe && (
              <div className="uni-card__grid">
                <UniPickList
                  label="Groups listed on For Me"
                  options={groups.rows.map((g) => ({
                    value: g.id,
                    label: g.name,
                    sub: `${fmtCount(g.member_count)} people`,
                  }))}
                  selected={draft.audience_group_ids}
                  onChange={(v) => set('audience_group_ids', v)}
                  emptyText="No audience groups yet — add one below, or ask a colleague to share theirs."
                  hint="The same group can be listed on many forms. Shared groups appear here too."
                />
                <UniPickList
                  label="Individual people"
                  options={userOptions}
                  selected={draft.audience_user_ids}
                  onChange={(v) => set('audience_user_ids', v)}
                  placeholder="Search people…"
                  hint="Added on top of whoever the groups already cover."
                />
              </div>
            )}

            {state?.can_lock && (
              <UniCheckField
                label="Lock this audience (admins only)"
                checked={!!draft.visibility_locked}
                onChange={(v) => set('visibility_locked', v)}
                hint="Stops the form's own owner from re-publishing an internal form."
              />
            )}

            <Note tone="info">
              Sign-in required: <strong>{state?.effective_requires_login ? 'yes' : 'no'}</strong>
              {' · '}Current setting: <strong>{state?.visibility_display || draft.visibility}</strong>
            </Note>
          </>
        )}
      </Card>

      <UniSection id="audience-groups" title="Audience groups" />
      <p className="uni-card__sub" style={{ marginTop: 0 }}>
        Groups are reusable — attach the same set of people to as many forms
        as you need, and share a group with colleagues (view, edit, or delete).
      </p>
      <UniToolbar>
        <UniActionButton primary onClick={createGroup.start}>+ New group</UniActionButton>
      </UniToolbar>
      <UniFormError error={groupAction.error} />
      <UniFormError error={shareAction.error} />
      <UniSection title="My groups" />
      <UniTable
        rows={mineGroups} loading={groups.loading} error={groups.error}
        columns={groupColumns} rowKey="id"
        empty="No groups of yours yet"
        emptyHint="Create a group once, then allocate it to every form those people should see."
      />
      <UniSection title="Shared with me" />
      <UniTable
        rows={sharedGroups} loading={groups.loading} error={null}
        columns={groupColumns} rowKey="id"
        empty="Nothing shared with you"
        emptyHint="When a colleague shares an audience group, it appears here and you can put it on your forms."
      />

      {/* Create group */}
      <UniModal
        open={createGroup.open}
        title="New audience group"
        onClose={createGroup.close}
        footer={
          <ModalFooter
            busy={createGroup.busy}
            onCancel={createGroup.close}
            onSave={() => createGroup.submit({
              ...createGroup.draft,
              name: createGroup.draft.name.trim(),
              form_ids: createGroup.draft.form_ids || [],
            })}
            saveLabel="Create group"
            disabled={!createGroup.draft.name.trim()}
          />
        }
      >
        <UniFormError error={createGroup.error} />
        <UniTextField
          label="Name" required autoFocus
          value={createGroup.draft.name}
          onChange={(v) => createGroup.set('name', v)}
          placeholder="e.g. North zone field team"
        />
        <UniTextArea
          label="Notes"
          value={createGroup.draft.description}
          onChange={(v) => createGroup.set('description', v)}
          placeholder="What is this group for?"
        />
        <UniSelectField
          label="Used for"
          value={createGroup.draft.purpose}
          onChange={(v) => createGroup.set('purpose', v)}
          options={GROUP_PURPOSE_OPTIONS}
        />
        <UniPickList
          label="People"
          options={userOptions}
          selected={createGroup.draft.members}
          onChange={(v) => createGroup.set('members', v)}
          placeholder="Search people…"
        />
        <UniPickList
          label="Use on these forms"
          options={manageableFormOptions}
          selected={createGroup.draft.form_ids}
          onChange={(v) => createGroup.set('form_ids', v)}
          emptyText="No forms you can edit yet."
          placeholder="Search forms…"
          hint="A group is not tied to one form — pick every form these people should see."
        />
      </UniModal>

      {/* Edit membership */}
      <UniModal
        open={!!editGroup}
        title={editGroup ? `People in “${editGroup.name}”` : ''}
        onClose={() => setEditGroup(null)}
        footer={
          <ModalFooter
            busy={groupAction.busy}
            onCancel={() => setEditGroup(null)}
            onSave={saveGroupMembers}
            saveLabel="Save people"
          />
        }
      >
        <UniFormError error={groupAction.error} />
        {editGroup && (
          <UniPickList
            label="Members"
            options={userOptions}
            selected={editGroup.members}
            onChange={(v) => setEditGroup((g) => ({ ...g, members: v }))}
            placeholder="Search people…"
            height={260}
          />
        )}
      </UniModal>

      {/* Allocate to forms */}
      <UniModal
        open={!!editGroupForms}
        title={editGroupForms ? `Forms using “${editGroupForms.name}”` : ''}
        onClose={() => setEditGroupForms(null)}
        footer={
          <ModalFooter
            busy={groupAction.busy}
            onCancel={() => setEditGroupForms(null)}
            onSave={saveGroupForms}
            saveLabel="Save forms"
          />
        }
      >
        <UniFormError error={groupAction.error} />
        {editGroupForms && (
          <UniPickList
            label="Forms"
            options={manageableFormOptions}
            selected={editGroupForms.form_ids}
            onChange={(v) => setEditGroupForms((g) => ({ ...g, form_ids: v }))}
            placeholder="Search forms…"
            emptyText="No forms you can edit yet."
            height={260}
            hint="The same group can sit on many forms. Tick every form these people should see on For Me."
          />
        )}
      </UniModal>

      {/* Share group */}
      <UniModal
        open={!!shareGroup}
        title={shareGroup ? `Share — ${shareGroup.name}` : 'Share group'}
        onClose={() => setShareGroup(null)}
        footer={
          <ModalFooter
            busy={shareAction.busy}
            onCancel={() => setShareGroup(null)}
            onSave={createShareLink}
            saveLabel="Share"
            disabled={!shareGroup || !shareDraft.shared_with}
          />
        }
      >
        <UniFormError error={shareAction.error} />
        <p className="uni-card__sub">
          Choose who can see this group, and whether they may only use it
          on forms, edit its people, or delete it.
        </p>
        <UniSelectField
          label="Person"
          value={shareDraft.shared_with}
          onChange={(v) => setShareDraft((d) => ({ ...d, shared_with: v }))}
          options={[
            { value: '', label: '— pick someone —' },
            ...userOptions,
          ]}
        />
        <UniSelectField
          label="Access"
          value={shareDraft.permission}
          onChange={(v) => setShareDraft((d) => ({ ...d, permission: v }))}
          options={GROUP_SHARE_OPTIONS}
        />
        {shareLinks.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div className="uni-card__title">People with access</div>
            {shareLinks.map((row) => (
              <div key={row.id} className="uni-card__grid" style={{ marginTop: 8 }}>
                <div>
                  {row.shared_with_label || row.shared_with_email}
                  {' · '}
                  {row.permission}
                </div>
                <button
                  type="button"
                  className="uni-row-btn"
                  onClick={() => revokeShareLink(row.id)}
                  disabled={shareAction.busy}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </UniModal>

      {/* Group details / rename / delete */}
      <UniSpecViewModal row={viewingGroup} onClose={() => setViewingGroup(null)}
                        title="Group details" fields={GROUP_FIELDS} />
      <UniSpecEditModal edit={editGroupDetails} title="Edit group"
                        fields={GROUP_FIELDS} />
      <UniConfirmModal state={delGroup} title="Delete group"
                       confirmLabel="Delete group">
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Delete the group <strong>{delGroup.row?.name}</strong>?
          {delGroup.row?.member_count > 0 && (
            <>
              {' '}Its <strong>{fmtCount(delGroup.row.member_count)}</strong>{' '}
              member{delGroup.row.member_count === 1 ? '' : 's'} keep their
              accounts — only the grouping goes.
            </>
          )}
        </p>
        {/* A group can be load-bearing: if a form lists it on For Me,
            deleting it shrinks who sees that form there. */}
        <div className="uni-note uni-note--warn" style={{ marginTop: 12 }}>
          <span aria-hidden="true">💡</span>
          <span>
            If any form lists this group on For Me, those people will drop
            off that tab on every form that uses it. Check those forms
            afterwards.
          </span>
        </div>
      </UniConfirmModal>
    </UniPageShell>
  );
}



/* ====================================================================
   2. Cross-module figures
   ====================================================================
   Answers "take a figure from another module … for the day for which
   the form is filled", plus "make this option restricted or
   changeable".

   ``mode`` is the changeable/locked switch the request asks for and
   ``mode_locked_by_admin`` is the meta-switch that stops the form owner
   from flipping it back.  The catalogue drives the metric dropdown and
   flags metrics whose backing app isn't installed, so a tenant without
   Pharma SFA can see (and understand) why "Doctor calls" is greyed out.
   */

export function UniFormsFigures() {
  const { options: formOptions, loading: formsLoading } = useFormOptions();
  const [formId, setFormId] = React.useState('');
  const [metrics, setMetrics] = React.useState([]);
  const [preview, setPreview] = React.useState(null);
  const [previewDate, setPreviewDate] = React.useState(isoDate());
  const act = useUniAction();

  React.useEffect(() => {
    if (!formId && formOptions.length) setFormId(formOptions[0].value);
  }, [formOptions, formId]);

  React.useEffect(() => {
    getJSON(EP.CATALOGUE)
      .then((d) => setMetrics(d.metrics || []))
      .catch(() => setMetrics([]));
  }, []);

  const figures = useUniList(formId ? `${EP.FIGURES}?form=${formId}` : '', [formId]);
  const questions = useUniList(formId ? `${EP.QUESTIONS}?form=${formId}` : '', [formId]);

  const metricOptions = React.useMemo(
    () => metrics.map((m) => ({
      value: m.key,
      label: m.available ? m.label : `${m.label} (module not installed)`,
    })),
    [metrics],
  );

  const dateQuestionOptions = React.useMemo(() => ([
    { value: '', label: '— pick a date question —' },
    ...questions.rows
      .filter((q) => q.question_type === 'date')
      .map((q) => ({ value: q.id, label: q.text })),
  ]), [questions.rows]);

  const targetQuestionOptions = React.useMemo(() => ([
    { value: '', label: '— show as a read-only figure —' },
    ...questions.rows
      .filter((q) => ['number', 'short_answer', 'paragraph'].includes(q.question_type))
      .map((q) => ({ value: q.id, label: q.text })),
  ]), [questions.rows]);

  const create = useUniCreate(
    EP.FIGURES,
    {
      form: '', label: '', metric: '', date_basis: 'submission_date',
      date_question: '', target_question: '', mode: 'locked',
      mode_locked_by_admin: false, is_required: false, show_on_form: true,
      unit: '', decimals: 0, order: 0,
    },
    () => figures.reload(),
  );

  const startCreate = () => {
    create.start();
    // ``start`` resets to ``initial``; re-seed the parts that depend on
    // the current screen state (which form, which metric is usable).
    const firstUsable = metrics.find((m) => m.available) || metrics[0];
    setTimeout(() => create.setDraft((d) => ({
      ...d,
      form: formId,
      metric: firstUsable?.key || '',
      label: firstUsable?.label || '',
      unit: firstUsable?.unit || '',
      decimals: firstUsable?.decimals ?? 0,
    })), 0);
  };

  const onMetricChange = (key) => {
    const m = metrics.find((x) => x.key === key);
    create.setDraft((d) => ({
      ...d,
      metric: key,
      // Only auto-fill the label while the user hasn't typed their own.
      label: (!d.label || d.label === metrics.find((x) => x.key === d.metric)?.label)
        ? (m?.label || d.label)
        : d.label,
      unit: m?.unit ?? d.unit,
      decimals: m?.decimals ?? d.decimals,
    }));
  };

  const setMode = (row, mode) => act.run(async () => {
    await patchJSON(`${EP.FIGURES}${row.id}/`, { mode });
    figures.reload();
  }).catch(() => {});

  const toggleLock = (row) => act.run(async () => {
    await patchJSON(`${EP.FIGURES}${row.id}/`,
                    { mode_locked_by_admin: !row.mode_locked_by_admin });
    figures.reload();
  }).catch(() => {});

  const removeFigure = (row) => {
    if (!window.confirm(`Remove the figure “${row.label}”?`)) return;
    act.run(async () => {
      await deleteJSON(`${EP.FIGURES}${row.id}/`);
      figures.reload();
    }).catch(() => {});
  };

  const runPreview = () => act.run(async () => {
    const data = await getJSON(
      `${EP.FORMS}${formId}/figure-preview/?date=${previewDate}`,
    );
    setPreview(data);
  }).catch(() => setPreview(null));

  const columns = [
    { key: 'label', label: 'Figure',
      render: (r) => (
        <span>
          <span style={{ fontWeight: 600 }}>{r.label}</span>
          {r.unit && <span className="uni-num--muted"> ({r.unit})</span>}
        </span>
      ) },
    { key: 'metric_label', label: 'Source',
      render: (r) => (
        <span>
          {r.metric_label || r.metric}
          {!r.metric_available && (
            <> <UniStatusPill value="module missing" palette="red" /></>
          )}
        </span>
      ) },
    { key: 'date_basis', label: 'For which day',
      render: (r) => (r.date_basis === 'question'
        ? 'A date answered in the form'
        : 'The day the form is filled') },
    { key: 'mode', label: 'Respondent may edit?',
      render: (r) => (
        // Left enabled even when ``mode_locked_by_admin`` is on: the
        // API answers 403 for a non-admin, and ``UniFormError`` above
        // spells that out.  Greying it out here instead would leave a
        // *form admin* — who legitimately may change it — unable to,
        // because the row alone can't tell us the caller's level.
        <select
          className="ty-select uni-filter__select"
          value={r.mode}
          disabled={act.busy}
          onChange={(e) => setMode(r, e.target.value)}
        >

          {FIGURE_MODE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) },
    { key: 'mode_locked_by_admin', label: 'Admin lock', align: 'center',
      render: (r) => (
        <button type="button" className="uni-row-btn"
                onClick={() => toggleLock(r)} disabled={act.busy}>
          {r.mode_locked_by_admin ? '🔒 Locked' : '🔓 Open'}
        </button>
      ) },
    { key: 'is_required', label: 'Required', align: 'center',
      render: (r) => <UniBool value={r.is_required} /> },
    { key: 'actions', label: '', align: 'right',
      render: (r) => (
        <span className="uni-row-actions">
          <button type="button" className="uni-row-btn"
                  onClick={() => removeFigure(r)} disabled={act.busy}>
            Remove
          </button>
        </span>
      ) },
  ];

  const unavailable = metrics.filter((m) => !m.available);

  return (
    <UniPageShell
      theme={THEME}
      icon="🔗"
      title="Figures from other modules"
      subtitle="Pull a number straight out of another module — like doctor calls from Pharma SFA for the day the form is filled."
      actions={
        <>
          <UniActionButton hero to="/uni-forms/forms">📝 Forms</UniActionButton>
          <UniActionButton hero to="/uni-forms">← Dashboard</UniActionButton>
        </>
      }
    >
      <UniToolbar>
        <UniFilterSelect
          label="Form"
          value={formId}
          onChange={(v) => { setFormId(v); setPreview(null); }}
          width={280}
          options={formsLoading && !formOptions.length
            ? [{ value: '', label: 'Loading…' }]
            : formOptions.length
              ? formOptions
              : [{ value: '', label: 'No forms yet' }]}
        />
        <UniActionButton primary onClick={startCreate}>+ Add figure</UniActionButton>
      </UniToolbar>

      <UniFormError error={act.error} />

      {unavailable.length > 0 && (
        <Note tone="warn">
          {unavailable.length} figure{unavailable.length === 1 ? '' : 's'} in the
          catalogue need a module this deployment doesn't have installed
          ({unavailable.map((m) => m.label).join(', ')}). They stay listed so
          the same form definition still works on a deployment that has them.
        </Note>
      )}

      {/* ``useUniList('')`` never fires a request, so its ``loading``
          flag would stay true forever while no form is picked — pass
          false in that case so the "pick a form" prompt shows instead
          of an endless spinner. */}
      <UniTable
        rows={figures.rows}
        loading={formId ? figures.loading : false}
        error={figures.error}
        columns={columns} rowKey="id"
        empty={formId ? 'No figures on this form' : 'Pick a form'}
        emptyHint="Add one to pre-fill a number from Pharma SFA, tasks or any other module, for the date the form covers."
      />

      <UniSection title="Preview" />
      <Card
        title="Resolve for a date"
        sub="Runs the same resolver the fill-form uses, as you, so you can confirm the numbers before publishing."
        actions={
          <>
            <input
              className="ty-input"
              type="date"
              value={previewDate}
              onChange={(e) => setPreviewDate(e.target.value)}
              style={{ width: 170 }}
            />
            <button type="button" className="uni-row-btn"
                    onClick={runPreview} disabled={!formId || act.busy}>
              Resolve
            </button>
          </>
        }
      >
        {!preview ? (
          <div className="uni-card__sub">
            Pick a date and hit “Resolve”.
          </div>
        ) : preview.figures?.length === 0 ? (
          <Note tone="info">This form has no figures marked "show on form".</Note>
        ) : (
          <UniTable
            rowKey="id"
            rows={preview.figures}
            columns={[
              { key: 'label', label: 'Figure' },
              { key: 'value', label: 'Value', align: 'right',
                render: (r) => (r.error
                  ? <span className="uni-pill uni-pill--red">unavailable</span>
                  : <span className="uni-num">{r.value ?? '—'}</span>) },
              { key: 'editable', label: 'Editable', align: 'center',
                render: (r) => <UniBool value={r.editable} /> },
              { key: 'error', label: 'Note',
                render: (r) => r.error || '—' },
            ]}
          />
        )}
      </Card>

      {/* Add figure */}
      <UniModal
        open={create.open}
        title="Add a figure from another module"
        onClose={create.close}
        width={600}
        footer={
          <ModalFooter
            busy={create.busy}
            onCancel={create.close}
            onSave={() => create.submit({
              ...create.draft,
              form: create.draft.form || formId,
              label: create.draft.label.trim(),
              date_question: create.draft.date_question || null,
              target_question: create.draft.target_question || null,
              decimals: Number(create.draft.decimals) || 0,
            })}
            saveLabel="Add figure"
            disabled={!create.draft.label.trim() || !create.draft.metric}
          />
        }
      >
        <UniFormError error={create.error} />
        <UniSelectField
          label="Figure to pull" required
          value={create.draft.metric}
          onChange={onMetricChange}
          options={metricOptions.length
            ? metricOptions
            : [{ value: '', label: 'No figures available' }]}
          hint={metrics.find((m) => m.key === create.draft.metric)?.description}
        />
        <UniTextField
          label="Label shown on the form" required
          value={create.draft.label}
          onChange={(v) => create.set('label', v)}
          placeholder="e.g. Doctor calls today"
        />
        <UniSelectField
          label="Which day should it cover?"
          value={create.draft.date_basis}
          onChange={(v) => create.set('date_basis', v)}
          options={DATE_BASIS_OPTIONS}
        />
        {create.draft.date_basis === 'question' && (
          <UniSelectField
            label="Date question" required
            value={create.draft.date_question}
            onChange={(v) => create.set('date_question', v)}
            options={dateQuestionOptions}
            hint="Add a date question to the form first if this list is empty."
          />
        )}
        <UniSelectField
          label="Locked or changeable"
          value={create.draft.mode}
          onChange={(v) => create.set('mode', v)}
          options={FIGURE_MODE_OPTIONS}
          hint="Locked figures are always re-read from the source module on submit."
        />
        <UniSelectField
          label="Write the value into"
          value={create.draft.target_question}
          onChange={(v) => create.set('target_question', v)}
          options={targetQuestionOptions}
          hint="Optional — pre-fills an existing question instead of adding a read-only row."
        />
        <div className="uni-form-row">
          <UniTextField
            label="Unit"
            value={create.draft.unit}
            onChange={(v) => create.set('unit', v)}
            placeholder="e.g. calls"
          />
          <UniTextField
            label="Decimal places"
            value={String(create.draft.decimals)}
            onChange={(v) => create.set('decimals', v.replace(/[^0-9]/g, '') || '0')}
          />
        </div>
        <UniCheckField
          label="Show on the form"
          checked={!!create.draft.show_on_form}
          onChange={(v) => create.set('show_on_form', v)}
          hint="Off means the figure is captured on submit but never displayed."
        />
        <UniCheckField
          label="Required — block submit if it can't be resolved"
          checked={!!create.draft.is_required}
          onChange={(v) => create.set('is_required', v)}
        />
        <UniCheckField
          label="Lock the locked/changeable switch (admins only)"
          checked={!!create.draft.mode_locked_by_admin}
          onChange={(v) => create.set('mode_locked_by_admin', v)}
          hint="Stops the form owner from making a locked figure editable later."
        />
      </UniModal>
    </UniPageShell>
  );
}


/* ====================================================================
   3. Admin reports
   ====================================================================
   Answers the last two asks: a report to the admin for each form type,
   and a customisable view — per person date-wise (latest submission
   wins when someone files twice), everyone for one specific date, or a
   group of people over a period.

   The builder runs against ``/reports/ad-hoc/`` while the admin is
   still choosing filters and only writes a ``FormReportView`` when they
   press "Save this view", so experimenting costs nothing.  Saved views
   re-run server-side (``/reports/<id>/run/``) and export the exact same
   rows as CSV, which keeps the screen and the download in step.
   */

const BLANK_REPORT = {
  scope: 'form',
  form: '',
  form_type: 'contact',
  report_type: 'flat_table',
  mode: 'person_date',
  latest_per_day: true,
  date_range: 'last_7',
  date_from: '',
  date_to: '',
  group: '',
  user_ids: [],
  columns: [],
  include_figures: true,
  include_incomplete: false,
  show_totals: true,
  frozen_rows: 1,
  frozen_columns: 0,
  export_spreadsheet_id: '',
  export_sheet_name: 'Report',
  config: {
    filter_slots: [
      { id: 'period', type: 'date', field: 'submitted_at', label: 'Submission period' },
    ],
    population: 'audience',
    pivot: { population: 'audience', response_strategy: 'latest' },
    sheet_export: {
      spreadsheet_id: '',
      sheet_name: 'Report',
    },
  },
};

function buildSavedReportColumns({
  onRun, onEdit, onDuplicate, onShare, onExport, onDelete, onUnlock, onLock,
  busy = false, shareBusy = false, duplicateBusyId = null,
}) {
  const canManageTemplate = (r) => r.permission === 'edit';
  const needsPin = (r) => r.pin_locked && !r.is_pin_unlocked;

  return [
    { key: 'name', label: 'Saved report',
      render: (r) => (
        <span>
          <span style={{ fontWeight: 600 }}>{r.name}</span>
          <ReportTemplatePinBadge locked={r.pin_locked} />
          {r.description && <div className="uni-card__sub">{r.description}</div>}
        </span>
      ) },
    { key: 'scope', label: 'Scope',
      render: (r) => r.form_title || (r.form_type ? `All “${r.form_type}” forms` : '—') },
    { key: 'report_type', label: 'Type',
      render: (r) => {
        if (r.report_type === 'flat_table') return 'flat table';
        if (r.report_type === 'pivot_by_user') return 'pivot by user';
        if (r.report_type === 'pivot_by_question') return 'pivot by question';
        if (r.report_type === 'summary') return 'summary';
        if (r.report_type === 'question_by_city') return 'question × city';
        return 'legacy fold';
      } },
    { key: 'mode', label: 'View',
      render: (r) => <UniStatusPill value={r.mode} palette="violet" /> },
    { key: 'date_range', label: 'Period',
      render: (r) => (r.date_range === 'custom'
        ? `${r.date_from || '?'} → ${r.date_to || 'today'}`
        : String(r.date_range).replace(/_/g, ' ')) },
    { key: 'audience_size', label: 'People', align: 'right',
      render: (r) => (r.audience_size
        ? <span className="uni-num">{fmtCount(r.audience_size)}</span>
        : 'everyone') },
    { key: 'is_shared_with_admins', label: 'Shared', align: 'center',
      render: (r) => <UniBool value={r.is_shared_with_admins} /> },
    { key: 'pin_locked', label: 'Lock', align: 'center',
      render: (r) => (r.pin_locked
        ? <span title="PIN locked">🔒</span>
        : <span className="uni-card__sub">—</span>) },
    { key: 'actions', label: '', align: 'right',
      render: (r) => (
        <span className="uni-row-actions">
          <button type="button" className="uni-row-btn"
                  onClick={() => onRun(r)} disabled={busy}>
            Run
          </button>
          {onEdit && (
            <button type="button" className="uni-row-btn"
                    onClick={() => (needsPin(r) ? onUnlock(r, 'edit') : onEdit(r))}
                    disabled={busy || !canManageTemplate(r)}>
              {needsPin(r) ? 'Unlock' : 'Edit'}
            </button>
          )}
          {onDuplicate && (
            <button type="button" className="uni-row-btn"
                    onClick={() => onDuplicate(r)}
                    disabled={busy || duplicateBusyId === r.id || r.can_duplicate === false}
                    title="Copy this template into a new one you own">
              {duplicateBusyId === r.id ? 'Duplicating…' : 'Duplicate'}
            </button>
          )}
          <button type="button" className="uni-row-btn"
                  onClick={() => onShare(r)}
                  disabled={shareBusy || r.can_manage_shares === false}>
            Share
          </button>
          {onLock && (
            <button type="button" className="uni-row-btn"
                    onClick={() => onLock(r)}
                    disabled={busy || !canManageTemplate(r)}>
              {r.pin_locked ? 'Manage lock' : 'Lock'}
            </button>
          )}
          <button type="button" className="uni-row-btn"
                  onClick={() => onExport(r, 'csv')} disabled={busy}>
            CSV
          </button>
          <button type="button" className="uni-row-btn"
                  onClick={() => onExport(r, 'xlsx')} disabled={busy}>
            XLSX
          </button>
          <button type="button" className="uni-row-btn"
                  onClick={() => (needsPin(r) ? onUnlock(r, 'delete') : onDelete(r))}
                  disabled={busy || !canManageTemplate(r)}>
            Delete
          </button>
        </span>
      ) },
  ];
}

function ReportTemplatePinModal({
  open, view, pin, setPin, busy, error, onClose, onSubmit,
}) {
  return (
    <UniModal
      open={open}
      title={view ? `Unlock — ${view.name}` : 'Unlock template'}
      onClose={onClose}
      footer={
        <ModalFooter
          busy={busy}
          onCancel={onClose}
          onSave={onSubmit}
          saveLabel="Unlock"
          disabled={!pin.trim()}
        />
      }
    >
      <UniFormError error={error} />
      <p className="uni-card__sub">
        This template is PIN-locked. Enter the PIN to edit or delete it.
        Viewing, running, sharing, duplicating, and exporting are not restricted.
      </p>
      <UniTextField
        label="PIN"
        value={pin}
        onChange={setPin}
        autoFocus
      />
    </UniModal>
  );
}

function useReportTemplatePinUnlock({ onUnlocked }) {
  const pinAction = useUniAction();
  const [pinOpen, setPinOpen] = React.useState(false);
  const [pinView, setPinView] = React.useState(null);
  const [pinValue, setPinValue] = React.useState('');
  const [pendingAction, setPendingAction] = React.useState(null);

  const closePin = React.useCallback(() => {
    setPinOpen(false);
    setPinView(null);
    setPinValue('');
    setPendingAction(null);
  }, []);

  const requestUnlock = React.useCallback((view, action) => {
    if (view.pin_locked && !view.is_pin_unlocked) {
      setPinView(view);
      setPendingAction(action);
      setPinValue('');
      setPinOpen(true);
      return true;
    }
    return false;
  }, []);

  const submitPin = () => pinAction.run(async () => {
    if (!pinView) return;
    const updated = await postJSON(`${EP.REPORTS}${pinView.id}/verify-pin/`, {
      pin: pinValue,
    });
    onUnlocked?.(updated, pendingAction);
    closePin();
  }).catch(() => {});

  return {
    pinOpen,
    pinView,
    pinValue,
    setPinValue,
    pinAction,
    closePin,
    requestUnlock,
    submitPin,
    pendingAction,
  };
}

function useReportTemplateShare({ onShared } = {}) {
  const { options: userOptions } = useUserOptions();
  const [shareOpen, setShareOpen] = React.useState(false);
  const [shareView, setShareView] = React.useState(null);
  const [shareLinks, setShareLinks] = React.useState([]);
  const [shareDraft, setShareDraft] = React.useState({
    shared_with: '', permission: 'view',
  });
  const shareAction = useUniAction();

  const publicShareUrl = (view) => {
    if (!view?.share_token_public) return '';
    const path = ROUTES.UNI_FORMS_TOKEN_REPORT(view.share_token_public);
    return `${window.location.origin}${path}`;
  };

  const openShare = (view) => {
    setShareView(view);
    setShareOpen(true);
    setShareLinks([]);
    setShareDraft({ shared_with: '', permission: 'view' });
    shareAction.run(async () => {
      setShareLinks(await getJSON(`${EP.REPORTS}${view.id}/shares/`));
    }).catch(() => {});
  };

  const createShareLink = () => shareAction.run(async () => {
    if (!shareDraft.shared_with) return;
    const created = await postJSON(`${EP.REPORTS}${shareView.id}/shares/`, {
      shared_with: shareDraft.shared_with,
      permission: shareDraft.permission,
    });
    setShareLinks((prev) => {
      const rest = prev.filter((s) => s.shared_with !== created.shared_with);
      return [created, ...rest];
    });
    setShareDraft({ shared_with: '', permission: 'view' });
    onShared?.();
  }).catch(() => {});

  const revokeShareLink = (shareId) => {
    if (!window.confirm('Stop sharing with this person?')) return;
    shareAction.run(async () => {
      await deleteJSON(`${EP.REPORTS}${shareView.id}/shares/${shareId}/`);
      setShareLinks((prev) => prev.filter((s) => s.id !== shareId));
    }).catch(() => {});
  };

  const rotatePublicToken = () => shareAction.run(async () => {
    const data = await postJSON(`${EP.REPORTS}${shareView.id}/share-token/rotate/`, {});
    setShareView(data);
  }).catch(() => {});

  const revokePublicToken = () => {
    if (!window.confirm('Revoke the public link? Anyone with it will lose access.')) return;
    shareAction.run(async () => {
      const data = await postJSON(`${EP.REPORTS}${shareView.id}/share-token/revoke/`, {});
      setShareView(data);
    }).catch(() => {});
  };

  return {
    userOptions,
    shareOpen, setShareOpen,
    shareView, shareLinks, shareDraft, setShareDraft,
    shareAction,
    openShare, createShareLink, revokeShareLink,
    rotatePublicToken, revokePublicToken, publicShareUrl,
  };
}

/** "Click Duplicate → POST → show the copy" state for a report template. */
function useDuplicateReportTemplate(reload) {
  const act = useUniAction();
  const [busyId, setBusyId] = React.useState(null);
  const [created, setCreated] = React.useState(null);

  const run = React.useCallback((row) => {
    setBusyId(row.id);
    setCreated(null);
    return act.run(() => postJSON(`${EP.REPORTS}${row.id}/duplicate/`, {}))
      .then((clone) => {
        setCreated(clone);
        reload();
        return clone;
      })
      .catch(() => null)
      .finally(() => setBusyId(null));
  }, [act, reload]);

  return {
    run, busyId, created,
    error: act.error,
    dismiss: () => setCreated(null),
  };
}

function DuplicatedReportNote({ state }) {
  const row = state.created;
  if (!row) return null;
  return (
    <Note>
      Copied to “{row.name}”. The original’s shares and PIN were not carried over.{' '}
      <UniActionButton to={ROUTES.UNI_FORMS_REPORT_TEMPLATE_EDIT(row.id)}>
        Edit the copy →
      </UniActionButton>
      {' '}
      <UniActionButton onClick={state.dismiss}>Dismiss</UniActionButton>
    </Note>
  );
}

function ReportTemplateShareModal({
  shareOpen, setShareOpen, shareView, shareLinks, shareDraft, setShareDraft,
  shareAction, userOptions, createShareLink, revokeShareLink,
  rotatePublicToken, revokePublicToken, publicShareUrl,
}) {
  return (
    <UniModal
      open={shareOpen}
      title={shareView ? `Share — ${shareView.name}` : 'Share report'}
      onClose={() => setShareOpen(false)}
      footer={
        <ModalFooter
          busy={shareAction.busy}
          onCancel={() => setShareOpen(false)}
          onSave={createShareLink}
          saveLabel="Share"
          disabled={!shareView || !shareDraft.shared_with}
        />
      }
    >
      <UniFormError error={shareAction.error} />
      <p className="uni-card__sub">
        Share with a colleague (view = run and filter only; edit = change the template).
        The public link is view-only and can be revoked.
      </p>
      <UniSelectField
        label="Person"
        value={shareDraft.shared_with}
        onChange={(v) => setShareDraft((d) => ({ ...d, shared_with: v }))}
        options={[
          { value: '', label: '— pick someone —' },
          ...userOptions,
        ]}
      />
      <UniSelectField
        label="Permission"
        value={shareDraft.permission}
        onChange={(v) => setShareDraft((d) => ({ ...d, permission: v }))}
        options={[
          { value: 'view', label: 'View only' },
          { value: 'edit', label: 'Can edit template' },
        ]}
      />
      {shareLinks.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="uni-card__title">People with access</div>
          {shareLinks.map((row) => (
            <div key={row.id} className="uni-card__grid" style={{ marginTop: 8 }}>
              <div>
                {row.shared_with_label || row.shared_with_email}
                {' · '}
                {row.permission}
              </div>
              <button
                type="button"
                className="uni-row-btn"
                onClick={() => revokeShareLink(row.id)}
                disabled={shareAction.busy}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
      {shareView?.share_token_public && (
        <div style={{ marginTop: 16 }}>
          <UniCopyField
            value={publicShareUrl(shareView)}
            label="Public view-only link"
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="uni-row-btn"
                    onClick={rotatePublicToken} disabled={shareAction.busy}>
              Rotate link
            </button>
            <button type="button" className="uni-row-btn"
                    onClick={revokePublicToken} disabled={shareAction.busy}>
              Revoke link
            </button>
          </div>
        </div>
      )}
      {shareView && !shareView.share_token_public && (
        <button type="button" className="uni-row-btn" style={{ marginTop: 12 }}
                onClick={rotatePublicToken} disabled={shareAction.busy}>
          Create public link
        </button>
      )}
    </UniModal>
  );
}

function ReportTemplateTables({
  mine, sharedWithMe, columns, mineEmptyHint, sharedEmptyHint,
}) {
  return (
    <>
      <UniSection title="My templates" />
      <UniTable
        rows={mine.rows} loading={mine.loading} error={mine.error}
        columns={columns} rowKey="id"
        empty="No templates of yours yet"
        emptyHint={mineEmptyHint}
      />
      <UniSection title="Shared with me" />
      <UniTable
        rows={sharedWithMe.rows} loading={sharedWithMe.loading} error={sharedWithMe.error}
        columns={columns} rowKey="id"
        empty="Nothing shared with you"
        emptyHint={sharedEmptyHint}
      />
    </>
  );
}

export function UniFormsReportTemplates() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewFromUrl = searchParams.get('view');

  const mine = useUniList(`${EP.REPORTS}?scope=mine`);
  const sharedWithMe = useUniList(`${EP.REPORTS}?scope=shared`);
  const run = useUniAction();

  const [filterCatalog, setFilterCatalog] = React.useState(null);
  const [filterValues, setFilterValues] = React.useState({ period: 'last_7' });
  const filtersHydrated = React.useRef(false);

  const [payload, setPayload] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [activeView, setActiveView] = React.useState(null);
  const [sheetRefreshNote, setSheetRefreshNote] = React.useState('');
  const [lockOpen, setLockOpen] = React.useState(false);
  const [lockView, setLockView] = React.useState(null);
  const [pdfExportOpen, setPdfExportOpen] = React.useState(false);
  const viewLoadedRef = React.useRef(null);

  const reloadLists = React.useCallback(() => {
    mine.reload();
    sharedWithMe.reload();
  }, [mine, sharedWithMe]);

  const share = useReportTemplateShare({ onShared: reloadLists });
  const dup = useDuplicateReportTemplate(reloadLists);

  const refreshViewState = React.useCallback((updated) => {
    setActiveView((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
    mine.reload();
    sharedWithMe.reload();
  }, [mine, sharedWithMe]);

  const pin = useReportTemplatePinUnlock({
    onUnlocked: (updated, action) => {
      refreshViewState(updated);
      if (action === 'edit') {
        navigate(ROUTES.UNI_FORMS_REPORT_TEMPLATE_EDIT(updated.id));
      } else if (action === 'delete') {
        if (!window.confirm(`Delete the saved report “${updated.name}”?`)) return;
        run.run(async () => {
          await deleteJSON(`${EP.REPORTS}${updated.id}/`);
          reloadLists();
        }).catch(() => {});
      }
    },
  });

  React.useEffect(() => {
    if (!viewFromUrl) return;
    if (filtersHydrated.current) return;
    filtersHydrated.current = true;
    const next = { period: 'last_7' };
    searchParams.forEach((v, k) => {
      if (!v || k === 'search' || k === 'view') return;
      next[k.startsWith('f.') ? k.slice(2) : k] = v;
    });
    setFilterValues(next);
  }, [searchParams, viewFromUrl]);

  React.useEffect(() => {
    if (!viewFromUrl || !filtersHydrated.current) return;
    const next = new URLSearchParams();
    next.set('view', viewFromUrl);
    Object.entries(filterValues || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      next.set(k, String(v));
    });
    setSearchParams(next, { replace: true });
  }, [filterValues, setSearchParams, viewFromUrl]);

  React.useEffect(() => {
    if (!activeView) return;
    const formParam = activeView.form ? `?form=${activeView.form}` : '';
    getJSON(`${EP.FILTER_CAT}${formParam}`)
      .then((d) => setFilterCatalog(d))
      .catch(() => setFilterCatalog(null));
  }, [activeView]);

  const filterSlots = activeView?.config?.filter_slots || payload?.filter_slots || [];

  const setFilterValue = (key, value) => {
    setFilterValues((f) => ({ ...f, [key]: value }));
  };

  const runActiveView = React.useCallback((view) => run.run(async () => {
    const fq = filterQueryString(filterValues);
    const q = [
      search ? `search=${encodeURIComponent(search)}` : '',
      fq,
    ].filter(Boolean).join('&');
    const data = await getJSON(
      `${EP.REPORTS}${view.id}/run/${q ? `?${q}` : ''}`,
    );
    setActiveView(view);
    setPayload(data);
  }).catch(() => {}), [run, search, filterValues]);

  React.useEffect(() => {
    if (!viewFromUrl) {
      viewLoadedRef.current = null;
      filtersHydrated.current = false;
      setActiveView(null);
      setPayload(null);
      return;
    }
    if (viewLoadedRef.current === viewFromUrl) return;
    viewLoadedRef.current = viewFromUrl;
    (async () => {
      try {
        const view = await getJSON(`${EP.REPORTS}${viewFromUrl}/`);
        const daySlot = (view.config?.filter_slots || []).find((s) => s.single_day);
        const filters = view.report_type === 'question_by_city'
          ? { [daySlot?.id || 'day']: daySlot?.default || 'today' }
          : { period: view.date_range || 'last_7' };
        setFilterValues(filters);
        setActiveView(view);
        const fq = filterQueryString(filters);
        const data = await getJSON(
          `${EP.REPORTS}${view.id}/run/${fq ? `?${fq}` : ''}`,
        );
        setPayload(data);
      } catch {
        setActiveView(null);
        setPayload(null);
      }
    })();
  }, [viewFromUrl]);

  const runSaved = React.useCallback((view) => {
    filtersHydrated.current = false;
    navigate(`${ROUTES.UNI_FORMS_REPORT_TEMPLATES}?view=${encodeURIComponent(view.id)}`);
  }, [navigate]);

  const clearRun = React.useCallback(() => {
    filtersHydrated.current = false;
    viewLoadedRef.current = null;
    setActiveView(null);
    setPayload(null);
    setSearch('');
    setSheetRefreshNote('');
    setPdfExportOpen(false);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const exportQueryString = () => [
    search ? `search=${encodeURIComponent(search)}` : '',
    filterQueryString(filterValues),
  ].filter(Boolean).join('&');

  const exportActive = (format = 'csv', pdfScale = 'as_is') => {
    if (!activeView) return;
    run.run(async () => {
      const formatParam = format === 'csv' ? '' : `export_format=${format}`;
      const scaleParam = format === 'pdf' && pdfScale && pdfScale !== 'as_is'
        ? `pdf_scale=${encodeURIComponent(pdfScale)}`
        : '';
      const q = [
        exportQueryString(),
        formatParam,
        scaleParam,
      ].filter(Boolean).join('&');
      const ext = format === 'xlsx' ? 'xlsx' : format === 'pdf' ? 'pdf' : 'csv';
      const fallback = format === 'pdf'
        ? reportPdfFallbackName(activeView.name)
        : `${activeView.name || 'report'}.${ext}`;
      await downloadFile(
        `${EP.REPORTS}${activeView.id}/export/${q ? `?${q}` : ''}`,
        fallback,
      );
      if (format === 'pdf') setPdfExportOpen(false);
    }).catch(() => {});
  };

  const refreshToSheet = () => {
    if (!activeView) return;
    run.run(async () => {
      const q = exportQueryString();
      const data = await postJSON(
        `${EP.REPORTS}${activeView.id}/export-sheet/${q ? `?${q}` : ''}`,
        {},
      );
      if (data?.url) {
        setSheetRefreshNote(
          `Updated ${fmtCount(data.row_count)} rows · ${data.url}`,
        );
      }
    }).catch(() => {});
  };

  const reportColumns = React.useMemo(() => (payload?.columns || []).map((c) => ({
    key: c.key,
    label: c.label,
    align: (
      c.key.startsWith('fig:')
      || c.key.startsWith('calc:')
      || c.key.startsWith('city:')
      || c.key === 'total'
      || c.key === 'submission_count'
    ) ? 'right' : 'left',
    render: (row) => {
      const v = row.cells?.[c.key];
      if (v === null || v === undefined || v === '') return '—';
      const isCountRow = row.response_id === '_response_count';
      if (typeof v === 'number') {
        return <span className={`uni-num${isCountRow ? ' uni-num--muted' : ''}`}>{v.toLocaleString()}</span>;
      }
      const shown = presentReportText(v, payload?.meta?.text_presentation, {
        questionColumn: isQuestionColumn(c.key),
      });
      const full = String(v);
      return shown === full ? shown : <span title={full}>{shown}</span>;
    },
  })), [payload]);

  const isMatrix = payload?.meta?.report_type === 'question_by_city';
  const totals = payload?.totals || {};
  const hasTotals = Object.keys(totals).length > 0;
  const hasSheetExport = activeView && (
    activeView.export_spreadsheet_id
    || activeView.config?.sheet_export?.spreadsheet_id
  );

  const exportSavedList = React.useCallback((view, format = 'csv') => run.run(async () => {
    const ext = format === 'xlsx' ? 'xlsx' : 'csv';
    await downloadFile(
      `${EP.REPORTS}${view.id}/export/${format === 'csv' ? '' : `?export_format=${format}`}`,
      `${view.name || 'report'}.${ext}`,
    );
  }).catch(() => {}), [run]);

  const removeSaved = React.useCallback((view) => {
    if (pin.requestUnlock(view, 'delete')) return;
    if (!window.confirm(`Delete the saved report “${view.name}”?`)) return;
    run.run(async () => {
      await deleteJSON(`${EP.REPORTS}${view.id}/`);
      reloadLists();
    }).catch(() => {});
  }, [run, reloadLists, pin.requestUnlock]);

  const editTemplate = React.useCallback((view) => {
    if (pin.requestUnlock(view, 'edit')) return;
    navigate(ROUTES.UNI_FORMS_REPORT_TEMPLATE_EDIT(view.id));
  }, [navigate, pin.requestUnlock]);

  const duplicateTemplate = React.useCallback((view) => {
    dup.run(view);
  }, [dup]);

  const handlePinUnlock = React.useCallback((view, action) => {
    pin.requestUnlock(view, action);
  }, [pin.requestUnlock]);

  const openLockModal = React.useCallback((view) => {
    setLockView(view);
    setLockOpen(true);
  }, []);

  const handleLockSaved = React.useCallback((updated) => {
    refreshViewState(updated);
  }, [refreshViewState]);

  const savedColumns = React.useMemo(
    () => buildSavedReportColumns({
      onRun: runSaved,
      onEdit: editTemplate,
      onDuplicate: duplicateTemplate,
      onShare: share.openShare,
      onExport: exportSavedList,
      onDelete: removeSaved,
      onUnlock: handlePinUnlock,
      onLock: openLockModal,
      busy: run.busy,
      shareBusy: share.shareAction.busy,
      duplicateBusyId: dup.busyId,
    }),
    [runSaved, editTemplate, duplicateTemplate, share.openShare, exportSavedList, removeSaved, handlePinUnlock, openLockModal, run.busy, share.shareAction.busy, dup.busyId],
  );

  const showingRun = Boolean(activeView || viewFromUrl);
  const runPeriod = payload?.meta
    ? (payload.meta.period_label || formatReportPeriod(payload.meta))
    : '';
  const runGenerated = payload?.meta ? formatGeneratedLabel(payload.meta) : '';

  return (
    <UniPageShell
      theme={THEME}
      icon="📋"
      title={activeView ? (
        payload?.meta ? (
          <span className="uni-report-runhead">
            <span className="uni-report-runhead__name">
              {activeView.name}
              <ReportTemplatePinBadge locked={activeView.pin_locked} />
            </span>
            <span className="uni-report-runhead__period">{runPeriod}</span>
            <span className="uni-report-runhead__when">
              {runGenerated ? `Generated ${runGenerated}` : ''}
            </span>
          </span>
        ) : (
          <span>
            {activeView.name}
            <ReportTemplatePinBadge locked={activeView.pin_locked} />
          </span>
        )
      ) : 'Report templates'}
      subtitle={activeView
        ? (activeView.description || activeView.form_title || 'Saved report template')
        : 'Saved report views you created, templates shared with you, and templates other administrators shared with every administrator.'}
      actions={
        showingRun ? (
          <>
            {activeView && (
              <>
                <UniActionButton
                  onClick={() => openLockModal(activeView)}
                  disabled={run.busy || activeView.permission !== 'edit'}
                >
                  {activeView.pin_locked ? 'Manage lock' : 'Lock template'}
                </UniActionButton>
                <UniActionButton
                  onClick={() => editTemplate(activeView)}
                  disabled={run.busy || activeView.permission !== 'edit'}
                >
                  {activeView.pin_locked && !activeView.is_pin_unlocked
                    ? 'Unlock to edit'
                    : 'Edit template'}
                </UniActionButton>
                <UniActionButton
                  onClick={() => duplicateTemplate(activeView)}
                  disabled={run.busy || dup.busyId === activeView.id || activeView.can_duplicate === false}
                >
                  {dup.busyId === activeView.id ? 'Duplicating…' : 'Duplicate'}
                </UniActionButton>
              </>
            )}
            <UniActionButton hero onClick={clearRun}>← All templates</UniActionButton>
          </>
        ) : (
          <UniActionButton hero to={ROUTES.UNI_FORMS_REPORT_TEMPLATE_NEW}>
            ＋ New template
          </UniActionButton>
        )
      }
    >
      {showingRun ? (
        <>
          <UniFormError error={dup.error} />
          <DuplicatedReportNote state={dup} />
          {activeView && filterSlots.length > 0 && (
            <ReportRuntimeFilters
              slots={filterSlots}
              values={filterValues}
              onChange={setFilterValue}
              catalog={filterCatalog}
            />
          )}

          {payload && activeView && (
            <>
              <div className="uni-toolbar" style={{ marginBottom: 12 }}>
                <span className="uni-card__sub" style={{ marginRight: 'auto' }}>
                  Export the current report (includes any filters above)
                </span>
                <button type="button" className="uni-row-btn"
                        onClick={() => exportActive('xlsx')} disabled={run.busy}>
                  Export Excel
                </button>
                <button type="button" className="uni-row-btn"
                        onClick={() => exportActive('csv')} disabled={run.busy}>
                  Export CSV
                </button>
                <button type="button" className="uni-row-btn"
                        onClick={() => setPdfExportOpen(true)} disabled={run.busy}>
                  Export PDF
                </button>
              </div>

              <UniToolbar onSearch={setSearch} placeholder="Filter people / answers…">
                <button type="button" className="uni-row-btn"
                        onClick={() => runActiveView(activeView)}
                        disabled={run.busy}>
                  ↻ Refresh
                </button>
                {hasSheetExport && (
                  <button type="button" className="uni-row-btn"
                          onClick={refreshToSheet} disabled={run.busy}>
                    ↻ Refresh sheet
                  </button>
                )}
              </UniToolbar>

              {sheetRefreshNote && <Note>{sheetRefreshNote}</Note>}

              {payload.meta?.truncated && (
                <Note tone="warn">
                  Showing the first {fmtCount(payload.meta.row_count)} rows — narrow
                  the period or the people to see the rest.
                </Note>
              )}

              {isMatrix && payload.meta?.unmatched_cities?.length > 0 && (
                <Note tone="warn">
                  Submissions from cities not in this template were excluded:{' '}
                  {payload.meta.unmatched_cities.join(', ')}.
                </Note>
              )}

              {isMatrix && (payload.meta?.anonymous_submission_count || 0) > 0 && (
                <Note tone="warn">
                  {fmtCount(payload.meta.anonymous_submission_count)} anonymous submission(s)
                  had no city and were excluded.
                </Note>
              )}

              <ReportResultsTable
                payload={{
                  ...payload,
                  freeze: payload.freeze || {
                    rows: activeView.frozen_rows ?? 1,
                    columns: activeView.frozen_columns ?? 0,
                  },
                }}
                columns={reportColumns}
                rowKey={payload.meta?.report_type === 'pivot_by_user' ? 'user_id' : 'response_id'}
              />

              {hasTotals && payload.rows?.length > 0 && (
                <div className="uni-card" style={{ marginTop: 12 }}>
                  <div className="uni-card__title">Totals</div>
                  <div className="uni-card__sub">
                    {reportColumns
                      .filter((c) => totals[c.key] !== undefined)
                      .map((c) => `${c.label}: ${Number(totals[c.key]).toLocaleString()}`)
                      .join('  ·  ')}
                  </div>
                </div>
              )}
            </>
          )}

          {!payload && viewFromUrl && (
            <div className="uni-state">
              <div className="uni-state__title">Loading report…</div>
            </div>
          )}
        </>
      ) : (
        <>
          <Note>
            Use the Lock action on a template to require a PIN before anyone
            can edit or delete it. Running, sharing, duplicating, and exporting
            still work without the PIN.
          </Note>
          <UniFormError error={dup.error} />
          <DuplicatedReportNote state={dup} />
          <ReportTemplateTables
            mine={mine}
            sharedWithMe={sharedWithMe}
            columns={savedColumns}
            mineEmptyHint='Click “New template” and follow the steps to create your first report.'
            sharedEmptyHint="Templates a colleague shared with you, and templates other administrators shared with every administrator, appear here."
          />
          <ReportTemplateShareModal {...share} />
        </>
      )}
      <ReportTemplatePinModal
        open={pin.pinOpen}
        view={pin.pinView}
        pin={pin.pinValue}
        setPin={pin.setPinValue}
        busy={pin.pinAction.busy}
        error={pin.pinAction.error}
        onClose={pin.closePin}
        onSubmit={pin.submitPin}
      />
      <ReportTemplateLockModal
        open={lockOpen}
        view={lockView}
        onClose={() => { setLockOpen(false); setLockView(null); }}
        onSaved={handleLockSaved}
      />
      <ReportPdfExportModal
        open={pdfExportOpen}
        busy={run.busy}
        onClose={() => setPdfExportOpen(false)}
        onExport={(scale) => exportActive('pdf', scale)}
      />
    </UniPageShell>
  );
}

